import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useRecoverPasswordMutation } from '@/store/api/Auth/Auth.query'
import type { forgotPasswordType } from '@/utils/schemas'
import { forgotPasswordSchema } from '@/utils/schemas'

export const ForgotPasswordForm = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const params = new URLSearchParams()
  params.append('token', 'holaSoyUnTokenDeRecoverPass')
  const form = useForm<forgotPasswordType>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  const {
    handleSubmit,
    control,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors = {} },
  } = form

  const [recoverPassword, { isLoading }] = useRecoverPasswordMutation()

  const recoverPasswordRequest = async (email: string) => {
    try {
      await recoverPassword({
        email,
      }).unwrap()
      toast.success(t('auth.forgotPassword.emailSent'))
      navigate(AppRoutes.forgotPaswordCheckYourInbox)
    } catch (e) {
      catchWithSentry(e)
      if (e.data.code === 'INVALID_CREDENTIALS') {
        toast.error(t('auth.forgotPassword.firstError'))
      } else {
        toast.error(t('auth.forgotPassword.error'))
      }
      console.warn(e)
    }
  }

  const onSubmit = (data: any) => {
    recoverPasswordRequest(data.email)
  }

  const handleBack = () => {
    navigate(AppRoutes.registerStart)
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-8">
      <div className="flex flex-col gap-5 w-full max-w-[436px] bg-baseColorHighlight p-8 rounded-3xl border border-neutralColor5">
        <div className="flex flex-col gap-5">
          <p className="text-[32px] max-md:text-2xl font-medium tracking-wide text-center">
            {t('auth.forgotPassword.title')}
          </p>
          <p className="text-base text-normal tracking-tight max-md:text-sm text-center">
            {t('auth.forgotPassword.subtitle')}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-5"
          >
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl className="w-full focus:outline-none rounded-3xl">
                    <Input
                      {...field}
                      placeholder={t('auth.forgotPassword.email')}
                    />
                  </FormControl>
                  <FormMessage data-name="email" />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="font-medium tracking-widest"
              isLoading={isLoading}
            >
              {t('auth.forgotPassword.continue')}
            </Button>
          </form>
        </Form>
      </div>
      <p
        className="text-center font-medium text-base text-neutralColor80 hover:text-neutralColor60 hover:cursor-pointer"
        onClick={handleBack}
      >
        {t('common.back')}
      </p>
    </div>
  )
}
